import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';

import {
  Deal,
  DealCustomField,
  GetDealResponse,
  IDealResource,
  OrdersPerDay,
  UpsertDealParams,
  UpsertDealResponse,
} from './deal.types';

import { lastValueFrom } from 'rxjs';
import { CrmResource } from '../crm/crm.resource';
import { ordersPerDayValues } from './deal.utils';
import { HttpError } from '@shared/exceptions/HttpError';
import { PixelResource } from '@infra/pixel';
import { QueryParamsResource } from '@infra/query-params';

@Injectable({
  providedIn: 'root',
})
export class DealResource implements IDealResource {

  private proxyUrl = environment.INBOUND_URL;

  /** facebook pixel */
  private fbp: string;
  /** tiktok pixel */
  private ttp: string;

  private shareableCode: string;
  private referrer: string;
  private readonly site_origem = 'Trial Anota AI';
  private readonly qa_mode: string;
  private readonly anotaai_reponde_version = environment.ANOTAAI_RESPONDE_DOWNLOAD_URL;

  constructor(
    private http: HttpClient,
    private crmResource: CrmResource,
    pixelResource: PixelResource,
    queryParamsResource: QueryParamsResource,
  ) {
    const queryParams = queryParamsResource.getCurrent();
    this.qa_mode = queryParams?.['qa_mode'];
    this.referrer = queryParams?.['ref'] || document?.referrer;
    this.shareableCode = queryParams?.['shareableCode'];

    this.fbp = pixelResource.getFacebookPixel();
    this.ttp = pixelResource.getTikTokPixel();
  }

  async upsertDeal(data: UpsertDealParams) {
    const body = {
      ...data,
      fbp: this.fbp,
      ttp: this.ttp,
      qa_mode: this.qa_mode,
      ref: this.referrer,
      site_origem: this.site_origem,
      anotaai_reponde_version: this.anotaai_reponde_version,
      computer: data.computer ? 'Sim' : 'Não',
      order_day: ordersPerDayValues(data.order_day),
    };

    const params: HttpParams = new HttpParams({ fromObject: { isTrial: true } });

    if (data.shareableCode || this.shareableCode) {
      params.set('shareableCode', data.shareableCode || this.shareableCode);
    }

    try {
      const resp = await lastValueFrom(
        this.http.post<UpsertDealResponse>(`${this.proxyUrl}/deals`, body, { params }),
      );

      return { deal_id: resp.data.dealId, ipAddress: resp.data.ipAddress };
    } catch (error) {
      throw new HttpError(error);
    }
  }

  async getDeal(dealId: string): Promise<Deal | undefined> {
    try {
      const res = await lastValueFrom(this.http.get<GetDealResponse>(`${this.proxyUrl}/deals/${dealId}`));

      if (!res?.data?.name || !res?.data?.contacts?.length) return;

      const {
        data: { id, customFields, ipAddress, companies, contacts, name, submit_form_data },
      } = res;

      const contact = contacts.find(({ email }) => !!email);
      const company = companies.find(({ name }) => !!name);
      const submitFormData = submit_form_data.find(({ force_billing_core_subscription, force_central_account, invoicing }) => !!force_billing_core_subscription || !!force_central_account || !!invoicing);

      const deal = { deal_id: id, name, first_name: name, ipAddress, invoicing: submitFormData?.invoicing, force_billing_core_subscription: submitFormData?.force_billing_core_subscription === "true", force_central_account: submitFormData?.force_central_account === "true"} as Deal;

      if (contact) {
        deal.email = contact.email.replace(/_invalidated/gi, ''); // remove all '_invalidated's substrings from email as it may have multiple
        deal.phone = contact.phone;
      }
      if (company) {
        deal.company = company.name;
      }

      const orders_per_day = this.findOrdersPerDayValue(customFields);

      if (orders_per_day) {
        deal.order_day = orders_per_day;
      }

      const computer = this.findHasComputerValue(customFields);

      if (computer !== undefined) {
        deal.computer = computer;
      }

      const analytics_id = customFields.find(field => field.key === environment.UUID_KEY)?.value;

      if (analytics_id) {
        this.crmResource.setAnalyticsId(analytics_id);
      }

      deal.analytics_id = this.crmResource.getAnalyticsId();

      return deal;
    } catch (error) {
      throw new HttpError(error);
    }
  }

  setShareableCode(value: string) {
    this.shareableCode = value;
  }

  /** get 'order_day' value from deal custom fields, this field doesn't have a key so we need to get it by value */
  private findOrdersPerDayValue(fields: DealCustomField[]): OrdersPerDay | undefined {
    // commented fields appear not being used
    const value = fields.find(
      field => field.value === 'Menos de 10' || field.value === 'Mais de 10',
      // field.value === '10 a 20' ||
      // field.value === '20 a 30' ||
      // field.value === '30 a 40' ||
      // field.value === '40 a 50' ||
      // field.value === '50 a 100' ||
      // field.value === 'Mais de 100',
    )?.value;

    return ordersPerDayValues(value);
  }

  /** get 'computer' value from deal custom fields, this field doesn't have a key could be 'Sim' or 'Não' */
  private findHasComputerValue(customFields: DealCustomField[]): boolean | undefined {
    const value = customFields.find(field => field.value === 'Sim' || field.value === 'Não')?.value;

    if (value) {
      return ['sim', 'true'].includes(String(value).toLowerCase());
    }

    return undefined;
  }
}
